import h from 'virtual-dom/h';
import {Listview, ListviewColumn} from "../../../listview";
import {customFetch as fetch} from "../../../fetch";
import {resolveCdnUrl} from "../../../cdn-url-resolver";
import IdentificationViewer from "./identificationViewer";
import moment from "moment/moment";
import PayrollEmployeesStatusManager from "./payrollEmployeesStatusManager";
import BasicEmployeeStatus from "./basicEmployeeStatus";
import numeral from "numeral";

let latestSearchBar = '';
let latestSelectedTab = 4;
let reloadEmployeeCounts: any;
let listView: any;

class PayrollEmployeesStatusListViewManager {
    static initCallback(reloadEmployeeCount: (newValue: string, tabsOnly: boolean) => Promise<void>){
        reloadEmployeeCounts = reloadEmployeeCount;
    }
    
    static createListview(selectedTab: number, searchBar: string, filterExpired: boolean): Listview {
        latestSearchBar = searchBar;
        latestSelectedTab = selectedTab;
        const columns = PayrollEmployeesStatusListViewManager.getColumnConfiguration(selectedTab);

        listView = new Listview({
            rootElementId: 'pending-contracts',
            pagerOptions: { pagerElementId: 'pending-contracts-pager' },
            getItems: async () => {
                const response = await fetch(`/Admin/EmployeesRegistrationStatus/GetEmployeesForTab/?selectedTab=${selectedTab}&searchBar=${searchBar}&filterExpired=${filterExpired.toString()}`);
                if (response.ok) {
                    return response.data;
                }
                return [];
            },
            columns: columns,
            defaultOrder: [['firstKnownStartDate', 'desc']],
            rowProps: (item) => {
                return item.awaitingWorkPermit ? { className: 'twv' } : {};
            },

        });

        return listView;
    }
    
    static reloadListView(){
        reloadEmployeeCounts(latestSearchBar, true);
    }

    static resetListView(contracts: Listview) { 
        if (contracts) {
            contracts.reset();  

            const contractListElement = document.querySelector('.pending-contracts'); // Assuming pagination is under a class named 'pagination'
            if (contractListElement) {
                let childs = contractListElement.childNodes
                childs.forEach(c => c.remove());
            }
            
            const paginationElement = document.querySelector('.pending-contracts-pager'); // Assuming pagination is under a class named 'pagination'
            if (paginationElement) {
                let childs = paginationElement.childNodes
                childs.forEach(c => c.remove());
            }
        }
    }

    private static getColumnConfiguration(selectedTab: number): ListviewColumn[] {
        // Define column configurations based on selectedTab
        let baseArray: ListviewColumn[] = [
            {
                position: 1,
                property: 'personalDetail.fullname',
                header: 'Medewerker',
                render: (item: BasicEmployeeStatus) => {                    
                    if (item.companyPlacements.length == 1) {
                       return h('div', {}, [
                           h('img', { className: 'user', src: resolveCdnUrl(item.avatarCdn) }, []),
                           h('a', { href: `/Admin/Employee/Detail/${item.companyPlacements[0].employeeId}`, target: '_blank'}, [
                               h('span', {}, `${item.personalDetail.fullname}`)
                           ]),
                       ]);
                    }
                    else {
                        return h('div', { className: 'flex' }, [
                            h('img', { className: 'user', src: resolveCdnUrl(item.avatarCdn) }, []),
                            h('div', {}, [
                                h('div', {}, [h('span', {}, `${item.personalDetail.fullname}`)]),
                                h('div', {}, item.companyPlacements.map((cp: any) => {
                                    return h('a', { href: `/Admin/Employee/Detail/${cp.employeeId}`, target: '_blank'}, [
                                        h('small', {}, [
                                            h('i', { className: 'fa fa-external-link-alt', style: 'margin-right: 2px' }, []),
                                            h('span', {}, `${cp.companyName}`),
                                            h('br', {}, [])
                                        ]),
                                    ])
                                }))
                            ])
                        ]);
                    }
                }
            },
            {
                position: 2,
                property: 'allCompanyNames',
                header: 'Bedrijf Plaatsingen',
                render: (item: BasicEmployeeStatus) => {
                    return h('div', {}, item.companyPlacements.map((cp: any) => ([
                       h('div', { className: 'flex padding-y-0.25x' }, ([
                           h('a', { href: `/Admin/Company/Users/${cp.companyId}`, target: '_blank' }, [
                               h('span', {}, cp.companyName)
                           ])
                       ]))
                    ])));
                }
            },
            {
                position: 3,
                property: 'firstKnownStartDate',
                header: 'Startdatum Contract',
                render: (item: BasicEmployeeStatus) => {
                    const firstKnownStartDate = item.firstKnownStartDate ?
                        moment(item.firstKnownStartDate).format('DD-MM-YYYY') :
                        '-';
                    
                    return h('a', {
                        title: firstKnownStartDate,
                        href: '#',
                        onclick: () => PayrollEmployeesStatusManager.openContractDetailsPopup(item)
                    }, firstKnownStartDate);
                }
            },
            {
                position: 4,
                property: 'latestKnownEndDate',
                header: 'Einddatum Contract',
                render: (item: BasicEmployeeStatus) => {
                    
                    const lastKnownEndDate = item.latestKnownEndDate 
                        ? moment(item.latestKnownEndDate).format('DD-MM-YYYY')
                        : '-';
                    
                    return h('a', {
                        title: lastKnownEndDate,
                        href: '#',
                        onclick: () => PayrollEmployeesStatusManager.openContractDetailsPopup(item)
                    }, lastKnownEndDate);
                }
            },
            {
                position: 5,
                property: 'personalDetail.age',
                header: 'Leeftijd',
                render: (item: BasicEmployeeStatus) => item.personalDetail.age.toString()
            },
            {
                position: 6,
                property: 'hourlyWage',
                header: 'Uurloon',
                sortable: false,
                render: (item: BasicEmployeeStatus) => {
                    const highestHourlyWage = Math.max(...item.companyPlacements.map((pb: any) => pb.hourlyWage));
                    const formatted = `${numeral(highestHourlyWage).format('$0,0.00')}/u`;
                    
                    return h('a', {
                        title: formatted,
                        href: '#',
                        onclick: () => PayrollEmployeesStatusManager.openContractDetailsPopup(item)
                    }, formatted);
                }
            },
            {
                position: 7,
                property: 'mostHoursPerWeek',
                header: 'Uren',
                sortable: true,
                render: (item: BasicEmployeeStatus) => `${item.mostHoursPerWeek}`
            },
            {
                position: 98,
                property: 'multipleCompanies',
                header: 'Meerdere bedrijven Info',
                sortable: false,
                render: (item: BasicEmployeeStatus) => item.multipleCompanies ? "Ja" : "Nee",
            },
        ];

        switch (selectedTab) {
            // AwaitingIdentification
            case 4:
                return baseArray.concat([
                {
                    position: 99,
                    props: { className: 'text-right' },
                    actions: [{
                        name: 'Legitimatie opnieuw versturen',
                        position: 5,
                        render: () => {
                            let icon = h('i', { className: 'fa fa-envelope' }, []);

                            return h('a', {
                                href: '#',
                                title: 'Legitimatie opnieuw versturen'
                            }, [icon]);
                        },
                        type: 'confirm-popup',
                        confirmText: 'Weet u zeker dat u deze medewerker opnieuw wilt uitnodigen?',
                        onConfirm: async (item: any) => {
                            await PayrollEmployeesStatusManager.resendInvite(item, PayrollEmployeesStatusListViewManager.reloadListView);
                        }
                    }]
                }]);
            // IdentificationDenied
            case 5:
            // IdentificationCheck
            case 6: 
                return baseArray.concat([
                    {
                        position: 8,
                        property: 'identification.nationality',
                        header: 'Nationaliteit',
                        render: (item: BasicEmployeeStatus) => {
                            if (item.identification.nationality === 0) {
                                return h('span', {}, 'Onbekend');
                            }
                            if (item.identification.nationality === 49) {
                                return h('span', {}, 'NL');
                            }
                            return h('span', {}, 'B');
                        }
                    },
                    {
                        position: 9,
                        property: 'idFileFront',
                        header: 'Legitimatiebewijs voorkant',
                        sortable: false,
                        render: (item: BasicEmployeeStatus) => {
                            if (item.identification.idFrontFileId !== "") {
                                return h('a', {
                                    onclick: () => IdentificationViewer.openIFrame(
                                        item.identification.idFrontFileId!,
                                        item.identification.documentNumber,
                                        item.identification.expirationDate,
                                        item.identification.bsn,
                                        item.personalDetail.birthday),
                                    title: 'Extra\'s legitimatiebewijs',
                                    href: '#'
                                }, 'Bekijk');
                            }
                            return h('span', {}, 'Geen bestand');
                        }
                    },
                    {
                        position: 10,
                        property: 'idFileBack',
                        header: 'Legitimatiebewijs achterkant',
                        sortable: false,
                        render: (item: BasicEmployeeStatus) => {
                            if (item.identification.idBackFileId !== "") {
                                return h('a', {
                                    onclick: () => IdentificationViewer.openIFrame(
                                        item.identification.idBackFileId!, 
                                        item.identification.documentNumber, 
                                        item.identification.expirationDate, 
                                        item.identification.bsn!, 
                                        item.personalDetail.birthday),
                                    title: 'Extra\'s legitimatiebewijs',
                                    href: '#'
                                }, 'Bekijk');
                            }
                            return h('span', {}, 'Geen bestand');
                        }
                    },
                    {
                        position: 11,
                        property: 'idFileExtra',
                        header: 'Legitimatiebewijs overig',
                        sortable: false,
                        render: (item: BasicEmployeeStatus) => {
                            if (item.identification.idExtraFileId !== "") {
                                return h('a', {
                                    onclick: () => IdentificationViewer.openIFrame(
                                        item.identification.idExtraFileId!, 
                                        item.identification.documentNumber, 
                                        item.identification.expirationDate, 
                                        item.identification.bsn, 
                                        item.personalDetail.birthday),
                                    title: 'Extra\'s legitimatiebewijs',
                                    href: '#'
                                }, 'Bekijk');
                            }
                            return h('span', {}, 'Geen bestand');
                        }
                    },
                    {
                        position: 99,
                        props: { className: 'text-right' },
                        actions: [
                            {
                                name: 'Legitimatie goedkeuren',
                                position: 3,
                                show: (item: BasicEmployeeStatus) => !!item.identification.idFrontFileId && !!item.identification.idBackFileId,
                                render: () => {
                                    let icon = h('i', {
                                        className: 'fa fa-check',
                                        style: 'margin-right: 10px;'
                                    }, []);
    
                                    return h('a', {
                                        href: '#',
                                        title: 'Legitimatie goedkeuren'
                                    }, [icon]);
                                },
                                type: 'confirm-popup',
                                confirmText: 'Weet u zeker dat u deze medewerker wilt goedkeuren?',
                                onConfirm: async (item: BasicEmployeeStatus) => {
                                    await PayrollEmployeesStatusManager.approveId(item.companyPlacements.map((cp: any) => cp.employeeId)[0], PayrollEmployeesStatusListViewManager.reloadListView);
                                }
                            }, 
                            {
                                name: 'Legitimatie afkeuren',
                                position: 4,
                                render: (item: BasicEmployeeStatus) => {
                                    let icon = h('i', { className: 'fa fa-times', style: 'margin-right: 10px;' }, []);
    
                                    return h('a', {
                                        href: '#',
                                        title: 'Legitimatie afkeuren',
                                        onclick: () => PayrollEmployeesStatusManager.openDenyIdPopup(item.companyPlacements.map((cp: any) => cp.employeeId)[0], PayrollEmployeesStatusListViewManager.reloadListView)
                                    }, [icon]);
                                }
                            },
                            {
                                name: 'Legitimatie opnieuw versturen',
                                position: 5,
                                render: () => {
                                    let icon = h('i', { className: 'fa fa-envelope' }, []);

                                    return h('a', {
                                        href: '#',
                                        title: 'Legitimatie opnieuw versturen'
                                    }, [icon]);
                                },
                                type: 'confirm-popup',
                                confirmText: 'Weet u zeker dat u deze medewerker opnieuw wilt uitnodigen?',
                                onConfirm: async (item: BasicEmployeeStatus) => {
                                    await PayrollEmployeesStatusManager.resendInvite(item, PayrollEmployeesStatusListViewManager.reloadListView);
                                }
                            }]
                    }
                ]);
            // NoContractSend
            case 7: 
                return baseArray.concat([
                {
                    position: 8,
                    property: 'contractNames',
                    header: 'Contract-types',
                    sortable: false,
                    render: (item: BasicEmployeeStatus) => {
                        const contractNames = item.companyPlacements.flatMap((pb: any) => pb.contractName).join(' - ');
                        const displayText = contractNames === "" || item.companyPlacements.length === 0 ? "Geen geselecteerd" : contractNames;

                        return h('a', {
                            title: 'Contracten',
                            href: '#',
                            onclick: () => PayrollEmployeesStatusManager.openContractTypesPopup(item.employmentAgreementId, PayrollEmployeesStatusListViewManager.reloadListView),
                        }, displayText);
                    }
                },
                {
                    position: 99,
                    props: { className: 'text-right' },
                    actions: [{
                        name: 'Contract versturen',
                        position: 1,
                        render: () => {
                            let icon = h('i', { className: 'fa fa-envelope' }, []);
        
                            return h('a', {
                                href: '#',
                                title: 'Contract versturen'
                            }, [icon]);
                        },
                        type: 'confirm-popup',
                        confirmText: 'Weet u zeker dat u dit contract wil verzenden?',
                        onConfirm: async (item: BasicEmployeeStatus) => {
                            await PayrollEmployeesStatusManager.sendContract(item, PayrollEmployeesStatusListViewManager.reloadListView);
                        }
                    }]
                }]);
            // AwaitingSigning
            case 8:
            // AwaitingExtensionSigning
            case 9: 
                return baseArray.concat([
                {
                    position: 99,
                    props: { className: 'text-right' },
                    actions: [{
                        name: 'Contract opnieuw versturen',
                        position: 99,
                        render: () => {
                            let icon = h('i', { className: 'fa fa-envelope' }, []);

                            return h('a', {
                                href: '#',
                                title: 'Contract opnieuw versturen'
                            }, [icon]);
                        },
                        type: 'confirm-popup',
                        confirmText: 'Weet u zeker dat u dit contract opnieuw wil verzenden?',
                        onConfirm: async (item: BasicEmployeeStatus) => {
                            await PayrollEmployeesStatusManager.resendContract(item, PayrollEmployeesStatusListViewManager.reloadListView);
                        }
                    }]
                }]);
            // SignedNotInAfas
            case 10: 
                return baseArray.concat([
                    {
                        position: 8,
                        property: 'idFileFront',
                        header: 'Heeft Identificatie Bewijs',
                        sortable: false,
                        render: (item: BasicEmployeeStatus) => item.identification.hasUploadedId ? "Ja" : "Nee"
                    },
                    {
                        position: 9,
                        property: 'mainLocation',
                        header: 'Hoofdlocatie',
                        sortable: false,
                        render: (item: BasicEmployeeStatus) => item.companyPlacements.map((pb: any) => pb.mainBranchName).join(' - ')
                    },
                    {
                        position: 10,
                        property: 'location',
                        header: 'Alle locaties',
                        sortable: false,
                        render: (item: BasicEmployeeStatus) => item.companyPlacements.map((pb: any) => pb.branchNames.join(', ')).join(' - ')
                    },
                    {
                        position: 99,
                        props: { className: 'text-right' },
                        actions: [{
                            name: 'Medewerker doorsturen naar AFAS',
                            position: 6,
                            render: (item: BasicEmployeeStatus) => {

                                if (!!item.processing)
                                {
                                    return h('img', { 'src': '/images/loading2.gif', 'style': 'width: 16px; height: 16px;' }, []);
                                }
                                else if (item.confirmed) {
                                    const icon = h('i', { className: 'fa fa-check', style: 'margin-left: 10px; color: green;' }, []);
                                    return h('span', { title: 'Bevestigd' }, [icon]);
                                }
                                else
                                {
                                    const icon = item.errored ? h('i', { className: ' fa fa-times', style: 'margin-left: 10px; color: red;' }, []) : h('i', { className: ' fa fa-upload', style: 'margin-left: 10px' }, []);
                                    return h('a', {
                                        href: '#',
                                        title: 'Medewerker doorsturen naar AFAS',
                                        onclick: async () => {
                                            item.processing = true;
                                            listView.build();

                                            await PayrollEmployeesStatusManager.sendEmployeesToAfas(
                                                item.companyPlacements.map((pb: any) => pb.employeeId),
                                                item.employmentAgreementId,
                                                () => {
                                                    item.confirmed = true;
                                                    item.errored = false;
                                                    item.processing = false;
                                                    listView.build();
                                                    PayrollEmployeesStatusListViewManager.reloadListView();
                                                },
                                                () => {
                                                    item.errored = true;
                                                    item.processing = false;
                                                    listView.build();
                                                });
                                        }
                                    }, [icon]);
                                }
                            }
                        },]
                    }
                ]);
            // InAfasMissingPlacements
            case 11:
                return baseArray.concat([
                    {
                        position: 8,
                        property: 'mainLocation',
                        header: 'Hoofdlocatie',
                        render: (item: BasicEmployeeStatus) => item.companyPlacements.map((pb: any) => pb.mainBranchName).join(' - ')
                    },
                    {
                        position: 9,
                        property: 'location',
                        header: 'Alle locaties',
                        render: (item: BasicEmployeeStatus) => item.companyPlacements.map((pb: any) => pb.branchNames.join(', ')).join(' - ')
                    },
                    {
                        position: 99,
                        props: { className: 'text-right' },
                        actions: [{
                            name: 'AFAS plaatsingen aanmaken voor medewerker',
                            position: 8,
                            render: (item: BasicEmployeeStatus) => {

                                if (!!item.processing)
                                {
                                    return h('img', { 'src': '/images/loading2.gif', 'style': 'width: 16px; height: 16px;' }, []);
                                }
                                else if (item.confirmed) {
                                    const icon = h('i', { className: 'fa fa-check', style: 'margin-left: 10px; color: green;' }, []);
                                    return h('span', { title: 'Bevestigd' }, [icon]);
                                }
                                else
                                {
                                    const icon = item.errored ? h('i', { className: ' fa fa-times', style: 'margin-left: 10px; color: red;' }, []) : h('i', { className: ' fa fa-upload', style: 'margin-left: 10px' }, []);
                                    return h('a', {
                                        href: '#',
                                        title: 'AFAS plaatsingen aanmaken voor medewerker',
                                        onclick: async () => {
                                            item.processing = true;
                                            listView.build();

                                            await PayrollEmployeesStatusManager.placeEmployeesToAfas(
                                                item.companyPlacements.map((pb: any) => pb.employeeId),
                                                () => {
                                                    item.confirmed = true;
                                                    item.errored = false;
                                                    item.processing = false;
                                                    listView.build();
                                                    PayrollEmployeesStatusListViewManager.reloadListView();
                                                },
                                                () => {
                                                    item.errored = true;
                                                    item.processing = false;
                                                    listView.build();
                                                });
                                        }
                                    }, [icon]);
                                }
                            }
                        }]
                    }
                ]);
            // InAfasOtherEmployee
            case 12:
                return baseArray.concat([
                    {
                        position: 8,
                        property: 'mainLocation',
                        header: 'Hoofdlocatie',
                        render: (item: BasicEmployeeStatus) => item.companyPlacements.map((pb: any) => pb.mainBranchName).join(' - ')
                    },
                    {
                        position: 9,
                        property: 'location',
                        header: 'Alle locaties',
                        render: (item: BasicEmployeeStatus) => item.companyPlacements.map((pb: any) => pb.branchNames.join(', ')).join(' - ')
                    },
                    {
                        position: 99,
                        props: { className: 'text-right' },
                        actions: [{
                            name: 'Medewerker doorsturen naar AFAS',
                            position: 6,
                            render: (item: BasicEmployeeStatus) => {
                                if (!!item.processing) {
                                    return h('img', { 'src': '/images/loading2.gif', 'style': 'width: 16px; height: 16px;' }, []);
                                }
                                else if (item.confirmed) {
                                    const icon = h('i', { className: 'fa fa-check', style: 'margin-left: 10px; color: green;' }, []);
                                    return h('span', { title: 'Bevestigd' }, [icon]);
                                }
                                else {
                                    const icon = item.errored ? h('i', { className: ' fa fa-times', style: 'margin-left: 10px; color: red;' }, []) : h('i', { className: ' fa fa-upload', style: 'margin-left: 10px' }, []);
                                    return h('a', {
                                        href: '#',
                                        title: 'Medewerker doorsturen naar AFAS',
                                        onclick: async () => {
                                            item.processing = true;
                                            listView.build();

                                            await PayrollEmployeesStatusManager.sendEmployeesToAfas(
                                                item.companyPlacements.map((pb: any) => pb.employeeId),
                                                item.employmentAgreementId,
                                                () => {
                                                    item.confirmed = true;
                                                    item.errored = false;
                                                    item.processing = false;
                                                    listView.build();
                                                    PayrollEmployeesStatusListViewManager.reloadListView();
                                                },
                                                () => {
                                                    item.errored = true;
                                                    item.processing = false;
                                                    listView.build();
                                                });
                                        }
                                    }, [icon]);
                                }
                            }
                        },]
                    }
                ]);
            default:
                return baseArray;
        }
    }
}

export default PayrollEmployeesStatusListViewManager;
